import React from 'react';

import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { PlusCircleIcon } from '@heroicons/react/24/outline';

import Badge from '@components/Base/Badge';
import { Checkbox } from '@components/Base/Checkbox';
import { CustomColumnDef } from '@components/Base/Table/types';
import TextDescription from '@components/Base/TextDescription';
import User from '@components/Base/UserDetails';
import { convertToTitleCase } from '@components/ProctorNgSessions/SessionTable/helper';
import { Exam } from '@containers/exams/types';
import { classNames } from '@utils/classNames';
import { formatDate } from '@utils/dateFormat';
import gripHorizontalIcon from 'app/images/grip-horizontal.svg';
import './styles.css';

export const createColumns = (handleWorkflowClick: (workflow: Exam) => void): CustomColumnDef<Exam>[] => [
  {
    id: 'Name',
    header: () => 'Name',
    accessorKey: 'name',
    enableSorting: false,
    hidden: false,
    cell: ({ row }) => (
      <button aria-label="exam-title" data-testid="exam-name">
        {row.original.name}
      </button>
    ),
    size: 200,
    className:
      'text-left text-sm font-medium font-roboto text-gray-900 left-[0px] sticky attendee-column shadow-[inset_-1px_0px_0px_0px] shadow-gray-400/20',
    cellClassName:
      'text-left sticky left-[0px] attendee-column shadow-[inset_-1px_0px_0px_0px] z-[1] shadow-gray-400/20',
  },
  {
    id: 'Workflows',
    header: () => 'Workflows',
    accessorKey: 'workflows',
    enableSorting: false,
    cell: ({ row }) => {
      const workflowCount = row.original.workflows_aggregate.aggregate.count
        ? row.original.workflows_aggregate.aggregate.count
        : 0;
      return (
        <button
          data-testid="workflow-views-button"
          className="flex items-center cursor-pointer"
          onClick={() => handleWorkflowClick(row.original)}
        >
          <span className="text-sm font-medium font-roboto text-gray-900">
            <Badge type="open">{workflowCount}</Badge>
            <span className="text-blue-600 ml-1">View</span>
          </span>
        </button>
      );
    },
    size: 150,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Description',
    header: () => 'Description',
    accessorKey: 'description',
    enableSorting: false,
    cell: ({ row }) => (
      <TextDescription
        text={row.original.description}
        showToolTip={true}
        maxNumberOfLines={2}
        textClass="text-description-width"
      />
    ),
    size: 237,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Created By',
    header: () => 'Created By',
    accessorKey: 'Created By',
    enableSorting: false,
    cell: ({ row }) => {
      const email = row.original.created_by_details?.email
        ? row.original.created_by_details?.email
        : row.original.created_by_details?.name
          ? row.original.created_by_details?.name
          : '';
      return (
        <User
          data={{
            name: email || 'NA',
          }}
          size="sm"
          hideImage={false}
          additionalContent={row.original.created_at ? formatDate(new Date(row.original.created_at), 'withTime') : ''}
        />
      );
    },
    size: 150,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'menu',
    enableSorting: false,
    accessorKey: 'menu',
    size: 48,
    className: 'text-left right-0 sticky shadow-[inset_0px_0px_0px_-1px] shadow-gray-400/20',
    cellClassName: 'text-center right-0 sticky z-[1] shadow-[inset_0px_0px_0px_-1px] shadow-gray-400/20',
    header: ({ table }) => {
      return (
        <div>
          <Popover className="flex justify-end items-center pr-3">
            <PopoverButton className={classNames('focus:outline-none')}>
              <PlusCircleIcon
                className={classNames('text-black h-5 w-5 focus:outline-none')}
                aria-hidden="true"
                data-testid="hide-column"
              />
            </PopoverButton>
            <Transition>
              <PopoverPanel>
                <div
                  className="bg-white border-solid border-[1px] popover-width font-normal shadow-sm max-h-[480px] overflow-y-auto rounded-md px-4 absolute right-0 top-12"
                  data-testid="column-model"
                >
                  {table.getAllLeafColumns().map((column) => {
                    if (column.id === 'menu') {
                      return null;
                    }
                    return (
                      <div key={column.id} className="h-[40px] w-full flex items-center">
                        <span className="pr-2 w-6 h-6">
                          <img src={gripHorizontalIcon} alt="edit" className="w-full h-full" />
                        </span>
                        <Checkbox
                          label={
                            typeof column.columnDef.header === 'string'
                              ? column.columnDef.header
                              : convertToTitleCase(column.id)
                          }
                          id={column.id}
                          size="md"
                          checked={column.getIsVisible()}
                          onChange={column.getToggleVisibilityHandler()}
                          disabled={false}
                        />
                      </div>
                    );
                  })}
                </div>
              </PopoverPanel>
            </Transition>
          </Popover>
        </div>
      );
    },
  },
];
